import { getCachedSession } from '../../session/cache';
import { getCurrentSiteIdOrThrow } from '../../sites';
/**
 * Check if the user is allowed to perform an action on an account permission.
 *
 * @param permission The permission that needs to be checked
 * @param actions The actions that need to be present
 */
export function hasAccountPermissions(permission, actions) {
    var _a;
    const session = getCachedSession();
    if (!session) {
        // No cached session? No permission!
        return false;
    }
    if (session.permissions.actions[permission]) {
        // If the actions contain the permission
        for (const action of actions) {
            // Compare actions
            if (!((_a = session.permissions.actions[permission]) === null || _a === void 0 ? void 0 : _a.includes(action))) {
                // If 1 of the actions is not found, reject
                return false;
            }
        }
        return true; // All input actions are found in the permissions
    }
    return false; // The permission could not be found
}
/**
 * Check if the user is allowed to perform an action on a site permission.
 *
 * If siteId is omitted, it will perform the check on the current active site
 *
 * @param permission The permission that needs to be checked
 * @param actions The action that needs to be present
 * @param siteId The (optional) siteId of the site for which the user should have permissions.
 */
export function hasSitePermissions(permission, actions, siteId = getCurrentSiteIdOrThrow()) {
    var _a;
    const session = getCachedSession();
    if (!session) {
        // No cached session? No permission!
        return false;
    }
    const site = session.sites.find((sessionSite) => {
        // Find the site matching the provided siteId
        if (sessionSite.site_id === siteId) {
            return sessionSite;
        }
        return undefined;
    });
    if (!site) {
        // No match? No permission!
        return false;
    }
    if (site.permissions.actions[permission]) {
        for (const action of actions) {
            if (!((_a = site.permissions.actions[permission]) === null || _a === void 0 ? void 0 : _a.includes(action))) {
                // If 1 of the actions is not found, reject
                return false;
            }
        }
        return true; // All input actions found in permissions
    }
    return false; // The permission could not be found
}
/**
 * Check if the account has certain entitlements.
 *
 * @param entitlements A list of the features that should be checked for access
 */
export function accountHas(entitlements) {
    const session = getCachedSession();
    if (!session) {
        // No session? No features!
        return false;
    }
    for (const entitlement of entitlements) {
        if (session.features.indexOf(entitlement) === -1) {
            // If 1 of the features given is not present
            return false;
        }
    }
    return true; // All features given are present
}
/**
 * Check if user is allowed to perform an action.
 *
 * @param action Action that needs to be present for this permission
 * @param level Level the permission applies to: 'site' or 'account
 * @param permission Name of the permission that needs to be checked
 * @param siteId (Optional) When level = 'site', specify on which site the permission needs to be checked
 */
export function userCan(actions, level, permission, siteId) {
    if (level === 'account') {
        return hasAccountPermissions(permission, actions);
    }
    else if (level === 'site') {
        return hasSitePermissions(permission, actions, siteId);
    }
    return false; // Unknown level
}
